import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { TruncateTypography } from "components/TruncateTypography";
import { TasksQuery } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { calendarIconForTask, textColorForTask } from "../../Tasks/statusStyles";

type Task = TasksQuery["virtualEvents"]["items"][0];

function getWeekDays(weekStart: DateTime): DateTime[] {
  return Array.from({ length: 7 }, (_, i) => weekStart.plus({ days: i }));
}

function groupByDay(tasks: Task[], days: DateTime[]): Map<string, Task[]> {
  const map = new Map<string, Task[]>();
  for (const d of days) {
    const iso = d.toISODate();
    if (iso) map.set(iso, []);
  }
  for (const task of tasks) {
    const iso = DateTime.fromISO(task.deadline).toISODate();
    if (iso && map.has(iso)) {
      map.get(iso)?.push(task);
    }
  }
  return map;
}

export const TasksWeekCalendar = ({
  loading,
  tasks,
  onWeekChange,
}: {
  loading: boolean;
  tasks: Task[];
  onWeekChange: (start: DateTime, end: DateTime) => void;
}) => {
  const theme = useTheme();
  const [weekStart, setWeekStart] = useState(() =>
    DateTime.local().startOf("week").minus({ days: 1 })
  );

  const days = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const tasksByDay = useMemo(() => groupByDay(tasks, days), [tasks, days]);

  const navigateWeek = (direction: number) => {
    const newStart = weekStart.plus({ weeks: direction });
    setWeekStart(newStart);
    onWeekChange(newStart, newStart.plus({ days: 6 }));
  };

  const weekLabel = `${days[0].toFormat("LLL d")} – ${days[6].toFormat("LLL d, yyyy")}`;

  return (
    <Box sx={{ pb: 4 }}>
      <Stack direction="row" alignItems="center" sx={{ px: 1, py: 1.25 }}>
        <Stack direction="row" spacing={0.5} paddingRight={1}>
          <IconButton aria-label="Previous week" size="small" onClick={() => navigateWeek(-1)}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton aria-label="Next week" size="small" onClick={() => navigateWeek(1)}>
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        <Typography variant="h6">{weekLabel}</Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          border: `1px solid ${theme.palette.divider}`,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      >
        {days.map((day, idx) => (
          <Box key={idx} sx={{ p: 1, textAlign: "center" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "uppercase" }}
            >
              {day.toFormat("cccc")}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          minHeight: 200,
          borderLeft: `1px solid ${theme.palette.divider}`,
        }}
      >
        {days.map((day) => {
          const iso = day.toISODate();
          if (!iso) return null;
          const isToday = day.hasSame(DateTime.local(), "day");
          const dayTasks = tasksByDay.get(iso) ?? [];

          return (
            <Box
              key={iso}
              sx={{
                borderRight: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                p: 0.75,
                bgcolor: isToday ? theme.palette.action.selected : undefined,
              }}
            >
              <Box display="flex" justifyContent="center" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.primary">
                  {day.toFormat("LLL d")}
                </Typography>
              </Box>

              <Stack spacing={0.5}>
                {loading ? (
                  <Skeleton variant="text" />
                ) : (
                  dayTasks.map((task) => (
                    <Stack
                      key={task.id + iso}
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      overflow="hidden"
                    >
                      {calendarIconForTask(task)}
                      <TruncateTypography variant="caption" color={textColorForTask(task)}>
                        {task.title}
                      </TruncateTypography>
                    </Stack>
                  ))
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
